import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Calendar, Clock, CurrencyEur, Envelope, Confetti, ArrowRight } from "@phosphor-icons/react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const SeminarErfolgPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!sessionId) {
      setPaymentStatus("error");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/seminars/payment-status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === "paid") {
          setPaymentStatus("success");
          setBooking(data.booking);
        } else if (data.status === "expired") {
          setPaymentStatus("expired");
        } else if (pollCount < 10) {
          // Continue polling
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          setPaymentStatus("pending");
          setBooking(data.booking);
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        if (pollCount < 5) {
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          setPaymentStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount, API_URL]);

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />

      <section className="pt-32 pb-20">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto">
            {paymentStatus === "checking" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full"
                  />
                </div>
                <h1
                  className="text-3xl font-normal mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Zahlung wird überprüft...
                </h1>
                <p className="text-[#2C2C2C]/60">
                  Bitte warte einen Moment, wir bestätigen deine Zahlung.
                </p>
              </motion.div>
            )}

            {paymentStatus === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                  className="w-24 h-24 mx-auto mb-8 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <CheckCircle size={48} className="text-white" weight="fill" />
                </motion.div>

                <Confetti size={40} className="text-[#D4AF37] mx-auto mb-4" weight="fill" />

                <h1
                  className="text-4xl font-normal mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="success-title"
                >
                  Buchung erfolgreich!
                </h1>
                <p className="text-lg text-[#2C2C2C]/70 mb-8">
                  Vielen Dank für deine Anmeldung zum Tressen Seminar!
                </p>

                {booking && (
                  <div className="bg-white p-8 rounded-2xl border border-[#E6E2D6] text-left mb-8">
                    <h2
                      className="text-xl font-medium mb-6"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      Deine Buchungsdetails
                    </h2>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <Calendar size={20} className="text-[#D4AF37]" />
                        <span><strong>Seminar:</strong> {booking.seminar_name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <Calendar size={20} className="text-[#D4AF37]" />
                        <span><strong>Datum:</strong> 13. April 2026</span>
                      </div>
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <Clock size={20} className="text-[#D4AF37]" />
                        <span><strong>Uhrzeit:</strong> 10:00 Uhr</span>
                      </div>
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <CurrencyEur size={20} className="text-[#D4AF37]" />
                        <span><strong>Bezahlt:</strong> {booking.amount} €</span>
                      </div>
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <Envelope size={20} className="text-[#D4AF37]" />
                        <span><strong>Name:</strong> {booking.name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[#2C2C2C]/80">
                        <Envelope size={20} className="text-[#D4AF37]" />
                        <span><strong>E-Mail:</strong> {booking.email}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-[#D4AF37]/10 p-6 rounded-xl border border-[#D4AF37]/30 mb-8">
                  <p className="text-[#2C2C2C]/80">
                    <strong>Was passiert jetzt?</strong><br />
                    Du erhältst in Kürze eine Bestätigungs-E-Mail mit allen Details zum Seminar.
                    Bei Fragen kannst du uns jederzeit kontaktieren.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <motion.button
                      className="btn-primary inline-flex items-center gap-2"
                      whileHover={{ scale: 1.05 }}
                    >
                      Zur Startseite
                      <ArrowRight size={18} />
                    </motion.button>
                  </Link>
                  <Link to="/kontakt">
                    <motion.button
                      className="btn-secondary inline-flex items-center gap-2"
                      whileHover={{ scale: 1.05 }}
                    >
                      Kontakt aufnehmen
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            )}

            {paymentStatus === "pending" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Clock size={40} className="text-yellow-600" />
                </div>
                <h1
                  className="text-3xl font-normal mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Zahlung wird verarbeitet
                </h1>
                <p className="text-[#2C2C2C]/60 mb-6">
                  Deine Zahlung wird noch verarbeitet. Du erhältst eine Bestätigung per E-Mail,
                  sobald die Zahlung abgeschlossen ist.
                </p>
                <Link to="/">
                  <motion.button
                    className="btn-primary"
                    whileHover={{ scale: 1.05 }}
                  >
                    Zur Startseite
                  </motion.button>
                </Link>
              </motion.div>
            )}

            {paymentStatus === "expired" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Clock size={40} className="text-red-500" />
                </div>
                <h1
                  className="text-3xl font-normal mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Sitzung abgelaufen
                </h1>
                <p className="text-[#2C2C2C]/60 mb-6">
                  Die Zahlungssitzung ist abgelaufen. Bitte versuche es erneut.
                </p>
                <Link to="/education">
                  <motion.button
                    className="btn-primary"
                    whileHover={{ scale: 1.05 }}
                  >
                    Zurück zum Seminar
                  </motion.button>
                </Link>
              </motion.div>
            )}

            {paymentStatus === "error" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <span className="text-4xl">❌</span>
                </div>
                <h1
                  className="text-3xl font-normal mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Ein Fehler ist aufgetreten
                </h1>
                <p className="text-[#2C2C2C]/60 mb-6">
                  Es gab ein Problem bei der Überprüfung deiner Zahlung.
                  Bitte kontaktiere uns, falls du bereits bezahlt hast.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/education">
                    <motion.button
                      className="btn-primary"
                      whileHover={{ scale: 1.05 }}
                    >
                      Erneut versuchen
                    </motion.button>
                  </Link>
                  <Link to="/kontakt">
                    <motion.button
                      className="btn-secondary"
                      whileHover={{ scale: 1.05 }}
                    >
                      Kontakt
                    </motion.button>
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeminarErfolgPage;
